/**
 * PDP Rebuy product add-ons — custom API widget
 * @see https://developers.rebuyengine.com/reference/api-custom-endpoints/
 */
(function () {
  const REBUY_API_KEY = "9d8aedf8fd00c6c5061909225832a20bb4b8de9d";
  const REBUY_ORIGIN = "https://rebuyengine.com";
  const REBUY_CACHED = "https://cached.rebuyengine.com";
  const METAFIELD_SECTION = "rebuy-addon-metafield";
  const DEFAULT_ADDON_LIMIT = 7;
  const MAX_ADDON_LIMIT_CAP = 25;
  const ADDON_LIGHTBOX_METAFIELD = "custom.addon_lightbox_description";
  const widgetConfigCache = new Map();
  const FLICKITY_READY_MS = 4000;
  const MODAL_OPEN_MS = 3000;

  function rebuyCartProperties(widgetId) {
    return {
      _source: "Rebuy",
      _attribution: "Rebuy Product",
      _widget_id: String(widgetId)
    };
  }

  // ——— Utilities ———

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function shopifyRoot() {
    return window.themeVariables?.routes?.rootUrlWithoutSlash || "";
  }

  function parsePriceToCents(value) {
    if (value == null || value === "") return 0;

    if (window.Rebuy?.util?.amountToCents) {
      try {
        const cents = window.Rebuy.util.amountToCents(value);
        if (Number.isFinite(cents)) return Math.max(0, Math.round(cents));
      } catch (error) {
        /* use fallback */
      }
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      if (Number.isInteger(value) && value >= 100) return value;
      return Math.round(value * 100);
    }

    const raw = String(value).trim();
    if (!raw) return 0;

    if (/^\d+$/.test(raw)) {
      const digits = parseInt(raw, 10);
      return digits >= 100 ? digits : digits * 100;
    }

    const amount = parseFloat(raw.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  function formatPrice(cents) {
    return typeof formatMoney === "function" ? formatMoney(cents) : `$${(cents / 100).toFixed(2)}`;
  }

  function imagesSignature(images) {
    return images.map((image) => image.src).join("|");
  }

  function racePromise(promise, ms) {
    return Promise.race([
      promise,
      new Promise((resolve) => setTimeout(() => resolve(null), ms))
    ]);
  }

  // ——— Product helpers ———

  function isVariantAvailable(variant) {
    return !!variant && variant.available !== false;
  }

  function isAddonProductAvailable(product) {
    if (product?.available === false) return false;
    return (product?.variants || []).some(isVariantAvailable);
  }

  function prepareAddonProduct(product) {
    if (!isAddonProductAvailable(product)) return null;
    const variants = (product.variants || []).filter(isVariantAvailable);
    return variants.length ? { ...product, variants } : null;
  }

  function filterAvailableAddonProducts(products) {
    return products.map(prepareAddonProduct).filter(Boolean);
  }

  function getSelectedVariant(product) {
    if (!product?.variants?.length) return null;

    if (product.selected_variant && isVariantAvailable(product.selected_variant)) {
      return product.selected_variant;
    }

    const id = product.selected_variant_id || product.variants[0]?.id;
    const match = product.variants.find((v) => String(v.id) === String(id));
    if (isVariantAvailable(match)) return match;

    return product.variants.find(isVariantAvailable) || null;
  }

  function lowestVariant(product) {
    return (product.variants || [])
      .filter(isVariantAvailable)
      .reduce((low, variant) => {
        const price = parsePriceToCents(variant.price);
        return !low || price < parsePriceToCents(low.price) ? variant : low;
      }, null);
  }

  function getVariantCompareAtPrice(variant) {
    return (
      variant?.compare_at_price ??
      variant?.compareAtPrice ??
      variant?.compare_price ??
      null
    );
  }

  function normalizeWidgetDiscount(discount) {
    if (!discount) return null;

    const amount = parseFloat(String(discount.amount ?? "").replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) return null;

    const type = String(discount.type || "percentage").toLowerCase();
    if (type === "none" || discount.enabled === false) return null;

    return {
      type: type === "fixed" ? "fixed_amount" : type,
      amount,
      discountedFrom: discount.discounted_from || "original_price"
    };
  }

  function getVariantBaselineCents(variant, discountedFrom) {
    const priceCents = parsePriceToCents(variant?.price);
    const compareCents = parsePriceToCents(getVariantCompareAtPrice(variant));

    if (discountedFrom === "compare_at_price" && compareCents > priceCents) {
      return compareCents;
    }

    return priceCents;
  }

  function applyWidgetDiscountCents(baselineCents, discount) {
    if (!discount || baselineCents <= 0) return baselineCents;

    switch (discount.type) {
      case "fixed_amount":
        return Math.max(0, baselineCents - Math.round(discount.amount * 100));
      case "price":
        return parsePriceToCents(discount.amount);
      case "percentage":
      default:
        return Math.round(baselineCents * (1 - discount.amount / 100));
    }
  }

  function getAddonVariantPricing(product, variant, widgetDiscount) {
    const priceCents = parsePriceToCents(variant?.price);
    const compareAtCents = parsePriceToCents(getVariantCompareAtPrice(variant));
    const shopifyOnSale = compareAtCents > priceCents && priceCents > 0;

    if (!widgetDiscount) {
      return {
        saleCents: priceCents,
        compareCents: shopifyOnSale ? compareAtCents : 0,
        onSale: shopifyOnSale
      };
    }

    const baselineCents = getVariantBaselineCents(variant, widgetDiscount.discountedFrom);
    const discountedCents = applyWidgetDiscountCents(baselineCents, widgetDiscount);
    let compareCents = baselineCents;

    if (shopifyOnSale && compareAtCents > compareCents) {
      compareCents = compareAtCents;
    }

    const onSale = compareCents > discountedCents && discountedCents > 0;

    return {
      saleCents: onSale ? discountedCents : priceCents,
      compareCents: onSale ? compareCents : 0,
      onSale
    };
  }

  function variantOnSale(product, variant, widgetDiscount) {
    return getAddonVariantPricing(product, variant, widgetDiscount).onSale;
  }

  function productHasVariablePricing(product, widgetDiscount) {
    if (!product?.variants || product.variants.length < 2) return false;
    const salePrices = product.variants
      .filter(isVariantAvailable)
      .map((variant) => getAddonVariantPricing(product, variant, widgetDiscount).saleCents);
    return new Set(salePrices).size > 1;
  }

  function getProductHandle(product) {
    if (product?.handle) return product.handle;
    if (product?.shopify_handle) return product.shopify_handle;

    const link = product?.link || product?.url || "";
    const match = String(link).match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }

  function normalizeImageEntry(image, fallbackAlt) {
    if (!image) return null;

    if (typeof image === "string") {
      return { src: image, alt: fallbackAlt || "" };
    }

    const src =
      image.src || image.url || image.preview_image?.src || image.featured_image?.src || image.featured_image;

    if (!src) return null;

    return {
      src,
      alt: image.alt || fallbackAlt || "",
      width: image.width || 800,
      height: image.height || 800
    };
  }

  function getVariantImage(product, variant) {
    if (!product || !variant) return null;

    const fromVariant =
      normalizeImageEntry(variant.featured_image, product.title) ||
      normalizeImageEntry(variant.image, product.title);
    if (fromVariant?.src) return fromVariant;

    const imageId = variant.image_id ?? variant.imageId;
    if (imageId != null) {
      const byImageId = (product.images || []).find((img) => String(img.id) === String(imageId));
      const normalized = normalizeImageEntry(byImageId, product.title);
      if (normalized?.src) return normalized;
    }

    const variantId = variant.id;
    if (variantId != null) {
      const byVariantId = (product.images || []).find((img) =>
        (img.variant_ids || []).some((id) => String(id) === String(variantId))
      );
      const normalized = normalizeImageEntry(byVariantId, product.title);
      if (normalized?.src) return normalized;
    }

    return null;
  }

  function getAddonDisplayImageSrc(product, variant) {
    const selected = variant || getSelectedVariant(product);
    const variantImage = getVariantImage(product, selected);
    if (variantImage?.src) return variantImage.src;

    return getProductImages(product)[0]?.src || "";
  }

  function getProductImages(product) {
    const variant = getSelectedVariant(product);
    const images = [];
    const seen = new Set();

    const add = (entry) => {
      const normalized = normalizeImageEntry(entry, product?.title);
      if (normalized?.src && !seen.has(normalized.src)) {
        seen.add(normalized.src);
        images.push(normalized);
      }
    };

    const variantImage = getVariantImage(product, variant);
    if (variantImage) add(variantImage);

    (product?.images || []).forEach(add);
    add(product?.image);
    add(product?.featured_image);

    return images;
  }

  function renderAddonPriceHtml(product, variant, widgetDiscount) {
    if (!variant) return "";

    const pricing = getAddonVariantPricing(product, variant, widgetDiscount);
    const fromLabel = productHasVariablePricing(product, widgetDiscount)
      ? '<span class="rokform-rebuy-addons__price-from">From </span>'
      : "";

    const salePrice = formatPrice(pricing.saleCents);
    const comparePrice = formatPrice(pricing.compareCents);

    if (pricing.onSale) {
      return `<span class="price price--highlight rokform-rebuy-addons__price--sale">${salePrice}</span><span class="price price--compare rokform-rebuy-addons__price--compare">${comparePrice}</span>`;
    }

    return `<span class="price rokform-rebuy-addons__price">${salePrice}</span>`;
  }

  function normalizeProductOptions(product) {
    const raw = product?.options;
    if (!raw?.length || !product?.variants?.length) return [];

    return raw.map((option, index) => {
      if (typeof option === "string") {
        const optionKey = `option${index + 1}`;
        const values = [...new Set(product.variants.map((v) => v[optionKey]).filter(Boolean))];
        return { name: option, values, position: index };
      }

      const position = option.position != null ? option.position - 1 : index;
      const optionKey = `option${position + 1}`;
      const values =
        option.values?.length > 0
          ? option.values
          : [...new Set(product.variants.map((v) => v[optionKey]).filter(Boolean))];

      return {
        name: option.name || `Option ${index + 1}`,
        values,
        position
      };
    });
  }

  function findVariantForOptionValue(product, optionIndex, value) {
    const optionKey = `option${optionIndex + 1}`;
    const matches = product.variants.filter((v) => v[optionKey] === value && isVariantAvailable(v));

    if (!matches.length) {
      const byTitle = product.variants.find((v) => v.title === value);
      return isVariantAvailable(byTitle) ? byTitle : null;
    }

    const selected = getSelectedVariant(product);
    if (!selected) return matches[0];

    const aligned = matches.find((variant) => {
      for (let i = 0; i < 3; i++) {
        if (i === optionIndex) continue;
        const key = `option${i + 1}`;
        const selectedValue = selected[key];
        if (selectedValue && variant[key] !== selectedValue) return false;
      }
      return true;
    });

    return aligned || matches[0];
  }

  function renderSwatchLabel(product, widgetId, itemIndex, optionIndex, value, variant, selectedVariant) {
    const checked =
      optionIndex == null
        ? selectedVariant && String(selectedVariant.id) === String(variant?.id)
        : selectedVariant &&
          (selectedVariant[`option${optionIndex + 1}`] === value ||
            String(selectedVariant.id) === String(variant?.id));
    const idPrefix = `rokform-addon-${widgetId}-${itemIndex}`;
    const radioName = optionIndex == null ? idPrefix : `${idPrefix}-${optionIndex}`;

    return `
      <label class="rokform-rebuy-addons__size-label${checked ? " is-selected" : ""}">
        <input
          type="radio"
          class="rokform-rebuy-addons__size-input"
          name="${radioName}"
          value="${variant.id}"
          data-product-id="${product.id}"
          ${checked ? "checked" : ""}
        />
        ${escapeHtml(value)}
      </label>
    `;
  }

  function buildRebuyAddonCartItem(product, widgetId, widgetDiscount) {
    const variant = getSelectedVariant(product);
    if (!isVariantAvailable(variant)) return null;

    const properties = rebuyCartProperties(widgetId);
    const pricing = getAddonVariantPricing(product, variant, widgetDiscount);

    if (pricing.onSale && pricing.saleCents > 0) {
      properties._rebuy_sale_price = String(pricing.saleCents);
      if (pricing.compareCents > pricing.saleCents) {
        properties._rebuy_compare_price = String(pricing.compareCents);
      }
    }

    return {
      id: Number(variant.id),
      quantity: 1,
      properties
    };
  }

  function buildMainCartItemFromFormData(formData) {
    const id = formData.get("id");
    if (!id) throw new Error("Variant id missing");

    const item = {
      id: Number(id),
      quantity: Number(formData.get("quantity") || 1)
    };

    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match) properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;

    const sellingPlan = formData.get("selling_plan");
    if (sellingPlan) item.selling_plan = Number(sellingPlan);

    return item;
  }

  // ——— Rebuy API ———

  async function fetchRebuyCacheKey(shop) {
    if (window.__rokformRebuyCacheKey) return window.__rokformRebuyCacheKey;

    const response = await fetch(`${REBUY_ORIGIN}/api/v1/user/config?shop=${encodeURIComponent(shop)}`);
    if (!response.ok) throw new Error("Could not load Rebuy shop config");

    const json = await response.json();
    const cacheKey = json?.data?.shop?.cache_key || "";
    window.__rokformRebuyCacheKey = cacheKey;
    return cacheKey;
  }

  function parseWidgetProductLimit(data) {
    const raw = data?.limit ?? data?.settings?.limit;
    const parsed = parseInt(String(raw), 10);
    if (!Number.isFinite(parsed) || parsed < 1) return DEFAULT_ADDON_LIMIT;
    return Math.min(parsed, MAX_ADDON_LIMIT_CAP);
  }

  function addonApiFetchLimit(displayLimit) {
    return Math.min(Math.max(displayLimit * 3, displayLimit), 50);
  }

  function parseRulesetIdFromWidgetData(data) {
    if (!data) return "";

    const fromEndpoint = String(data.endpoint || "").match(/\/custom\/id\/(\d+)/);
    if (fromEndpoint) return fromEndpoint[1];

    return (
      data.custom_endpoint_id ||
      data.data_source_id ||
      data.settings?.custom_endpoint_id ||
      data.settings?.data_source_id ||
      data.settings?.endpoint_id ||
      ""
    );
  }

  function parseRebuyWidgetConfig(data, widgetId) {
    const filterOos = data?.filter_oos;
    const requestMetafields = data?.metafields === true || data?.metafields === "yes";

    return {
      widgetId: String(widgetId),
      rulesetId: parseRulesetIdFromWidgetData(data),
      productLimit: parseWidgetProductLimit(data),
      discount: normalizeWidgetDiscount(data?.discount || data?.settings?.discount),
      filterOos: filterOos === true || filterOos === "yes" ? "yes" : "no",
      requestMetafields: requestMetafields || true,
      metafieldNsKey: ADDON_LIGHTBOX_METAFIELD,
      widgetType: data?.type || ""
    };
  }

  async function fetchRebuyWidgetConfig(apiKey, widgetId, cacheKey) {
    if (!widgetId) return null;

    const cacheKeyStr = `${widgetId}:${cacheKey || ""}`;
    if (widgetConfigCache.has(cacheKeyStr)) {
      return widgetConfigCache.get(cacheKeyStr);
    }

    const params = new URLSearchParams({ key: apiKey, id: widgetId });
    if (cacheKey) params.set("cache_key", cacheKey);

    let response = await fetch(`${REBUY_CACHED}/api/v1/widgets/settings?${params}`);
    if (!response.ok) {
      response = await fetch(`${REBUY_ORIGIN}/api/v1/widgets/settings?${params}`);
    }
    if (!response.ok) return null;

    const data = (await response.json())?.data || {};
    const config = parseRebuyWidgetConfig(data, widgetId);
    widgetConfigCache.set(cacheKeyStr, config);
    return config;
  }

  async function fetchAddonProducts({ apiKey, shop, productId, variantId, widgetConfig }) {
    const params = new URLSearchParams({
      key: apiKey,
      shopify_product_ids: String(productId),
      filter_oos: widgetConfig.filterOos,
      limit: String(addonApiFetchLimit(widgetConfig.productLimit))
    });

    if (widgetConfig.requestMetafields) {
      params.set("metafields", "yes");
      if (widgetConfig.metafieldNsKey) {
        params.set("metafield_ns_key", widgetConfig.metafieldNsKey);
      }
    }

    if (variantId) params.set("shopify_variant_ids", String(variantId));

    const dataSourceId = String(widgetConfig.rulesetId || "").trim();
    if (!dataSourceId) {
      throw new Error(
        `Could not find a Rebuy data source for widget ${widgetConfig.widgetId}. Link a data source to this widget in Rebuy admin.`
      );
    }

    const endpoint = `${REBUY_ORIGIN}/api/v1/custom/id/${dataSourceId}`;

    const response = await fetch(`${endpoint}?${params}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.error_message || `Rebuy API error (${response.status})`);
    }

    const json = await response.json();
    const products = json?.data || json?.products || [];
    return Array.isArray(products) ? products : [];
  }

  function getAddonPanels() {
    return [...document.querySelectorAll("rokform-rebuy-addons")];
  }

  function addonCatalogSignature(products) {
    return products
      .map((product) => {
        const variant = getSelectedVariant(product) || product.variants?.[0];
        const imageSrc = getAddonDisplayImageSrc(product, variant);
        return [product.id, variant?.id || "", imageSrc, parsePriceToCents(variant?.price)].join(":");
      })
      .join("|");
  }

  function normalizeImageUrl(url) {
    if (!url) return "";
    try {
      return new URL(url, window.location.origin).href;
    } catch (error) {
      return url;
    }
  }

  // ——— Lightbox ———

  function getLightbox() {
    return document.getElementById("rebuy-addon-lightbox");
  }

  function queryLightbox() {
    const modal = getLightbox();
    if (!modal) return null;

    return {
      modal,
      title: modal.querySelector("[data-rebuy-lightbox-title]"),
      description: modal.querySelector("[data-rebuy-lightbox-description]"),
      descriptionPanel: modal.querySelector(".rebuy-addon-lightbox__description-panel"),
      price: modal.querySelector("[data-rebuy-lightbox-price]"),
      media: modal.querySelector("[data-rebuy-lightbox-media]"),
      carousel: modal.querySelector("[data-rebuy-lightbox-carousel]"),
      controls: modal.querySelector("[data-rebuy-lightbox-controls]"),
      dots: modal.querySelector("[data-rebuy-lightbox-dots]")
    };
  }

  function resetLightboxState(ui) {
    ui.modal?.classList.remove("is-content-ready");
    ui.description?.classList.remove("is-visible");
    ui.descriptionPanel?.classList.remove("is-pending");
  }

  function setLightboxDescription(ui, html) {
    if (!ui?.description) return;

    const nextHtml = html || "";
    const unchanged = ui.description.innerHTML.trim() === nextHtml.trim();

    if (unchanged) {
      ui.descriptionPanel?.classList.remove("is-pending");
      if (nextHtml) ui.description.classList.add("is-visible");
      return;
    }

    ui.description.classList.remove("is-visible");
    requestAnimationFrame(() => {
      ui.description.innerHTML = nextHtml;
      ui.descriptionPanel?.classList.toggle("is-pending", !nextHtml);
      if (nextHtml) {
        requestAnimationFrame(() => ui.description.classList.add("is-visible"));
      }
    });
  }

  async function prepareLightboxContent(ui, product, widgetDiscount) {
    resetLightboxState(ui);

    if (ui.title) ui.title.textContent = product.title || "";

    const initialImages = getProductImages(product);
    await renderLightboxCarousel(ui, initialImages);
    renderLightboxPrice(ui, product, widgetDiscount);
    setLightboxDescription(ui, product.body_html || "");

    return {
      initialImages,
      handle: getProductHandle(product)
    };
  }

  function waitForModalOpen(modal, timeoutMs = MODAL_OPEN_MS) {
    const whenOpen = new Promise((resolve) => {
      if (modal?.hasAttribute("open")) {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
        return;
      }

      const observer = new MutationObserver(() => {
        if (modal?.hasAttribute("open")) {
          observer.disconnect();
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        }
      });

      observer.observe(modal, { attributes: true, attributeFilter: ["open"] });
    });

    return Promise.race([whenOpen, new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
  }

  function clearFlickityObserver(carousel) {
    if (!carousel?.intersectionObserver) return;
    carousel.intersectionObserver.disconnect();
    delete carousel.intersectionObserver;
  }

  async function destroyLightboxFlickity(carousel) {
    if (!carousel) return;

    carousel.classList.remove("flickity-enabled");
    clearFlickityObserver(carousel);

    if (!carousel.flickity) return;

    const instance = await racePromise(carousel.flickity, 200);
    if (instance?.destroy && instance.element) instance.destroy();
    carousel.flickity = null;
  }

  function waitForFlickityReady(carousel, timeoutMs = FLICKITY_READY_MS) {
    return new Promise((resolve) => {
      if (carousel.classList.contains("flickity-enabled")) {
        resolve(true);
        return;
      }

      let settled = false;
      const finish = (ready) => {
        if (settled) return;
        settled = true;
        carousel.removeEventListener("flickity:ready", onReady);
        clearTimeout(timer);
        resolve(ready);
      };

      const onReady = () => finish(true);
      const timer = setTimeout(() => finish(false), timeoutMs);
      carousel.addEventListener("flickity:ready", onReady, { once: true });
    });
  }

  async function initLightboxFlickity(carousel) {
    if (!carousel?._createFlickity || carousel.childElementCount < 2) return false;

    clearFlickityObserver(carousel);
    carousel._createFlickity();

    if (!(await waitForFlickityReady(carousel))) return false;

    const instance = await racePromise(carousel.flickityInstance, 500);
    instance?.resize?.();
    return !!instance;
  }

  async function ensureLightboxFlickity(carousel) {
    if (!carousel || carousel.childElementCount < 2) return false;

    for (let attempt = 0; attempt < 2; attempt++) {
      await destroyLightboxFlickity(carousel);
      if (await initLightboxFlickity(carousel)) return true;
    }

    console.warn("Add-on lightbox carousel could not initialize");
    return false;
  }

  function renderLightboxSlides(images) {
    return images
      .map(
        (image, index) => `
          <div
            id="rebuy-addon-lightbox-slide-${index}"
            class="rebuy-addon-lightbox__media-item${index === 0 ? " is-initial-selected is-selected" : ""}"
          >
            <div class="rebuy-addon-lightbox__image-wrapper">
              <img
                src="${escapeHtml(image.src)}"
                alt="${escapeHtml(image.alt)}"
                width="${image.width || 800}"
                height="${image.height || 800}"
                loading="${index === 0 ? "eager" : "lazy"}"
                class="rebuy-addon-lightbox__image"
              >
            </div>
          </div>
        `
      )
      .join("");
  }

  function renderLightboxDots(imageCount) {
    if (imageCount <= 1) return "";

    return Array.from({ length: imageCount }, (_, index) => `
      <button
        type="button"
        tabindex="-1"
        class="dots-nav__item tap-area"
        ${index === 0 ? 'aria-current="true"' : ""}
        aria-controls="rebuy-addon-lightbox-slide-${index}"
        data-action="select"
      >
        <span class="visually-hidden">Slide ${index + 1}</span>
      </button>
    `).join("");
  }

  async function renderLightboxCarousel(ui, images) {
    if (!ui?.media) return;

    const { carousel, controls, dots } = ui;

    if (!images.length) {
      if (carousel) {
        await destroyLightboxFlickity(carousel);
        carousel.innerHTML = "";
      }
      if (dots) dots.innerHTML = "";
      if (controls) controls.hidden = true;
      ui.media.hidden = true;
      return;
    }

    if (!carousel) return;

    await destroyLightboxFlickity(carousel);
    carousel.innerHTML = renderLightboxSlides(images);

    if (dots) dots.innerHTML = renderLightboxDots(images.length);
    if (controls) controls.hidden = images.length <= 1;

    ui.media.hidden = false;
    await ensureLightboxFlickity(carousel);
  }

  async function fetchProductJson(handle) {
    const response = await fetch(`${shopifyRoot()}/products/${handle}.js`);
    if (!response.ok) throw new Error("Product fetch failed");
    return response.json();
  }

  async function fetchAddonDescriptionHtml(handle) {
    const response = await fetch(
      `${shopifyRoot()}/products/${encodeURIComponent(handle)}?section_id=${METAFIELD_SECTION}`
    );
    if (!response.ok) return "";

    const doc = new DOMParser().parseFromString(await response.text(), "text/html");
    const content = doc.querySelector(".rebuy-addon-metafield");
    return content ? content.innerHTML.trim() : "";
  }

  function renderLightboxPrice(ui, product, widgetDiscount) {
    if (!ui?.price) return;

    const variant = getSelectedVariant(product) || product.variants?.[0];
    const priceHtml = renderAddonPriceHtml(product, variant, widgetDiscount);

    if (!priceHtml) {
      ui.price.hidden = true;
      return;
    }

    ui.price.innerHTML = priceHtml;
    ui.price.hidden = false;
  }

  async function openAddonLightbox(product, widgetDiscount) {
    const ui = queryLightbox();
    if (!ui || !product) return;

    let initialImages = [];
    let handle = "";

    try {
      ({ initialImages, handle } = await prepareLightboxContent(ui, product, widgetDiscount));
    } catch (error) {
      console.warn("Add-on lightbox prepare failed", error);
      return;
    }

    ui.modal.open = true;
    await waitForModalOpen(ui.modal);
    ui.modal.classList.add("is-content-ready");

    if (!handle) return;

    try {
      const [productJson, metafieldHtml] = await Promise.all([
        fetchProductJson(handle),
        fetchAddonDescriptionHtml(handle)
      ]);

      const shopifyImages = getProductImages({
        ...product,
        images: productJson.images,
        featured_image: productJson.featured_image,
        image: productJson.image
      });

      if (imagesSignature(shopifyImages) !== imagesSignature(initialImages)) {
        await renderLightboxCarousel(ui, shopifyImages);
      }

      setLightboxDescription(
        ui,
        metafieldHtml || product.body_html || productJson?.description || ""
      );
    } catch (error) {
      console.warn("Add-on lightbox load failed", error);
      setLightboxDescription(ui, product.body_html || "");
    }
  }

  // ——— Custom element ———

  class RokformRebuyAddons extends HTMLElement {
    constructor() {
      super();
      this._products = [];
      this._selected = new Map();
      this._widgetConfig = null;
      this._rebuyWidgetId = "";
      this._group = null;
      this._embedded = false;
      this._lastError = "";
      this._hasRenderedOnce = false;
      this._catalogSignature = "";
      this._variantRefreshTimer = null;
      this._onVariantChanged = this._onVariantChanged.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      this._listEl = this.querySelector("[data-list]");
      this._skeletonEl = this.querySelector("[data-skeleton]");
      this._emptyEl = this.querySelector("[data-empty]");
      this._errorEl = this.querySelector("[data-error]");
      this._group = this.closest("rokform-rebuy-addons-group");
      this._embedded = !!this._group;
      this._rebuyWidgetId = this.dataset.widgetId || "";

      const shop = this.dataset.shop || window.Shopify?.shop;
      if (shop) {
        this._cacheKeyPromise = fetchRebuyCacheKey(shop).catch(() => "");
      } else {
        this._cacheKeyPromise = Promise.resolve("");
      }

      document.addEventListener("variant:changed", this._onVariantChanged);
      this.addEventListener("change", this._onChange);
      this.addEventListener("click", this._onClick);
      this._loadProducts();
    }

    disconnectedCallback() {
      document.removeEventListener("variant:changed", this._onVariantChanged);
      this.removeEventListener("change", this._onChange);
      this.removeEventListener("click", this._onClick);
      clearTimeout(this._variantRefreshTimer);
    }

    _onVariantChanged(event) {
      const variantId = event.detail?.variant?.id;
      if (!variantId) return;

      const nextVariantId = String(variantId);
      if (nextVariantId === this.dataset.variantId && this._hasRenderedOnce) return;

      this.dataset.variantId = nextVariantId;
      clearTimeout(this._variantRefreshTimer);
      this._variantRefreshTimer = setTimeout(() => {
        this._loadProducts(nextVariantId, { silent: true });
      }, 120);
    }

    async _loadProducts(variantId, options = {}) {
      const silent = options.silent === true && this._hasRenderedOnce;
      const productId = this.dataset.productId;
      const shop = this.dataset.shop || window.Shopify?.shop;

      if (!this._rebuyWidgetId) {
        this._products = [];
        this._render();
        this._setLoading(false);
        return;
      }

      if (!productId || !shop) {
        this._showError("Add-ons could not be loaded.");
        return;
      }

      if (!silent) {
        this._setLoading(true);
      }

      this._hideError();
      this._dispatchPanelState();

      try {
        const cacheKey = await this._cacheKeyPromise;
        const widgetConfig = await fetchRebuyWidgetConfig(REBUY_API_KEY, this._rebuyWidgetId, cacheKey);
        if (!widgetConfig?.rulesetId) {
          throw new Error(
            `Could not find a Rebuy data source for widget ${this._rebuyWidgetId}. Link a data source to this widget in Rebuy admin.`
          );
        }

        this._widgetConfig = widgetConfig;

        const products = await fetchAddonProducts({
          apiKey: REBUY_API_KEY,
          shop,
          productId,
          variantId: variantId || this.dataset.variantId,
          widgetConfig
        });

        const nextProducts = filterAvailableAddonProducts(products)
          .slice(0, widgetConfig.productLimit)
          .map((product) => {
            const selectedVariant =
              getSelectedVariant(product) || lowestVariant(product) || product.variants[0];

            return {
              ...product,
              selected_variant_id: selectedVariant?.id,
              selected_variant: selectedVariant,
              selected: this._selected.get(String(product.id)) ?? false
            };
          });

        const nextSignature = addonCatalogSignature(nextProducts);
        const catalogUnchanged = nextSignature === this._catalogSignature && this._hasRenderedOnce;

        this._products = nextProducts;
        this._catalogSignature = nextSignature;

        if (!catalogUnchanged) {
          this._render();
          this._hasRenderedOnce = this._products.length > 0;
        } else {
          this._syncSelectionToDom();
        }

        this.dispatchEvent(
          new CustomEvent("rokform-rebuy-addons:ready", {
            bubbles: true,
            detail: { products: this._products, silent }
          })
        );
      } catch (error) {
        console.warn("rokform-rebuy-addons load failed", error);
        if (!silent) {
          this._showError(error.message || "Could not load add-on products.");
          this._products = [];
          this._catalogSignature = "";
          this._render();
        }
      } finally {
        if (!silent) {
          this._setLoading(false);
        } else {
          this._dispatchPanelState();
        }
      }
    }

    _syncSelectionToDom() {
      if (!this._listEl) return;

      this._products.forEach((product) => {
        const checkbox = this._listEl.querySelector(
          `.rokform-rebuy-addons__checkbox[data-product-id="${product.id}"]`
        );
        if (!checkbox) return;

        checkbox.checked = !!product.selected;
        checkbox.closest(".rokform-rebuy-addons__item")?.classList.toggle(
          "rokform-rebuy-addons__item--selected",
          checkbox.checked
        );
      });
    }

    _updateAddonItem(product, index) {
      const item = this._listEl?.querySelector(
        `.rokform-rebuy-addons__item[data-product-id="${product.id}"]`
      );
      if (!item) return false;

      const variant = getSelectedVariant(product);
      const priceEl = item.querySelector(".rokform-rebuy-addons__item-price");
      if (priceEl) {
        priceEl.innerHTML = renderAddonPriceHtml(product, variant, this._widgetConfig?.discount);
      }

      const imageSrc = getAddonDisplayImageSrc(product, variant);
      const img = item.querySelector(".rokform-rebuy-addons__item-image img");
      if (img && imageSrc) {
        const nextSrc = normalizeImageUrl(imageSrc);
        if (normalizeImageUrl(img.getAttribute("src")) !== nextSrc) {
          img.src = imageSrc;
        }
      }

      const main = item.querySelector(".rokform-rebuy-addons__item-main");
      if (main) {
        const variantsHtml = this._renderOptions(product, index, this._rebuyWidgetId);
        const existingVariants = main.querySelector(".rokform-rebuy-addons__variants");
        if (variantsHtml) {
          if (existingVariants) {
            existingVariants.outerHTML = variantsHtml;
          } else {
            const heading = main.querySelector(".rokform-rebuy-addons__item-heading");
            heading?.insertAdjacentHTML("afterend", variantsHtml);
          }
        } else {
          existingVariants?.remove();
        }
      }

      return true;
    }

    _dispatchPanelState() {
      if (!this._embedded || !this._group) return;

      this._group.dispatchEvent(
        new CustomEvent("rokform-rebuy-addons:panel-state", {
          bubbles: true,
          detail: {
            panel: this,
            loading: this.classList.contains("is-loading"),
            productCount: this._products.length,
            errorMessage: this._lastError
          }
        })
      );
    }

    _setLoading(isLoading) {
      this.classList.toggle("is-loading", isLoading);

      if (this._embedded) {
        this._dispatchPanelState();
        if (isLoading && this._listEl) this._listEl.hidden = true;
        return;
      }

      if (this._skeletonEl) this._skeletonEl.hidden = !isLoading;
      if (isLoading && this._listEl) this._listEl.hidden = true;
      this._dispatchPanelState();
    }

    _showError(message) {
      this._lastError = message || "";

      if (!this._embedded && this._errorEl) {
        this._errorEl.textContent = this._lastError;
        this._errorEl.hidden = false;
      }

      this._dispatchPanelState();
    }

    _hideError() {
      this._lastError = "";

      if (this._errorEl) {
        this._errorEl.hidden = true;
        this._errorEl.textContent = "";
      }

      this._dispatchPanelState();
    }

    _render() {
      if (!this._listEl) return;

      if (!this._products.length) {
        this._listEl.innerHTML = "";
        this._listEl.hidden = true;
        this.classList.add("is-empty-panel");
        if (!this._embedded && this._emptyEl) this._emptyEl.hidden = false;
        this._dispatchPanelState();
        return;
      }

      this.classList.remove("is-empty-panel");
      if (this._emptyEl) this._emptyEl.hidden = true;
      this._listEl.hidden = false;
      this._listEl.innerHTML = this._products.map((product, index) => this._renderItem(product, index)).join("");
      this._hasRenderedOnce = true;
      this._catalogSignature = addonCatalogSignature(this._products);
      this._dispatchPanelState();
    }

    _renderItem(product, index) {
      const variant = getSelectedVariant(product);
      const imageSrc = getAddonDisplayImageSrc(product, variant);
      const priceHtml = renderAddonPriceHtml(product, variant, this._widgetConfig?.discount);
      const optionsHtml = this._renderOptions(product, index, this._rebuyWidgetId);
      const selectedClass = product.selected ? " rokform-rebuy-addons__item--selected" : "";

      return `
        <article class="rokform-rebuy-addons__item${selectedClass}" data-product-id="${product.id}" data-index="${index}">
          <div class="rokform-rebuy-addons__item-price price-list">${priceHtml}</div>
          <button type="button" class="rokform-rebuy-addons__item-image" data-action="open-lightbox" data-index="${index}">
            <img src="${escapeHtml(imageSrc)}" alt="${escapeHtml(product.title)}" width="102" height="102" loading="lazy">
          </button>
          <div class="rokform-rebuy-addons__item-main">
            <button type="button" class="rokform-rebuy-addons__item-heading" data-action="open-lightbox" data-index="${index}">
              <span class="rokform-rebuy-addons__item-title">${escapeHtml(product.title)}</span>
            </button>
            ${optionsHtml}
          </div>
          <div class="rokform-rebuy-addons__item-checkbox">
            <label class="rokform-rebuy-addons__toggle-label">
              <input
                type="checkbox"
                class="rokform-rebuy-addons__checkbox"
                data-product-id="${product.id}"
                ${product.selected ? "checked" : ""}
                aria-label="Add ${escapeHtml(product.title)}"
              />
            </label>
          </div>
        </article>
      `;
    }

    _renderOptions(product, index, widgetId) {
      if (!product.variants || product.variants.length <= 1) return "";

      const normalizedOptions = normalizeProductOptions(product);
      const selectedVariant = getSelectedVariant(product);

      if (normalizedOptions.length) {
        return normalizedOptions
          .map((option, optionIndex) => {
            if (!option.values?.length) return "";

            const position = option.position ?? optionIndex;
            const swatches = option.values
              .map((value) => {
                const variant = findVariantForOptionValue(product, position, value);
                if (!variant) return "";
                return renderSwatchLabel(
                  product,
                  widgetId,
                  index,
                  position,
                  value,
                  variant,
                  selectedVariant
                );
              })
              .filter(Boolean)
              .join("");

            if (!swatches) return "";

            return `
              <div class="rokform-rebuy-addons__variants">
                <p class="rokform-rebuy-addons__variant-label">Choose ${escapeHtml(option.name)}:</p>
                <div class="rokform-rebuy-addons__swatches" role="group" aria-label="${escapeHtml(option.name)}">
                  ${swatches}
                </div>
              </div>
            `;
          })
          .join("");
      }

      const swatches = product.variants
        .map((variant) =>
          renderSwatchLabel(product, widgetId, index, null, variant.title, variant, selectedVariant)
        )
        .join("");

      return `
        <div class="rokform-rebuy-addons__variants">
          <p class="rokform-rebuy-addons__variant-label">Choose option:</p>
          <div class="rokform-rebuy-addons__swatches" role="group" aria-label="Variant">
            ${swatches}
          </div>
        </div>
      `;
    }

    _onChange(event) {
      const checkbox = event.target.closest(".rokform-rebuy-addons__checkbox");
      if (checkbox) {
        const product = this._products.find((p) => String(p.id) === String(checkbox.dataset.productId));
        if (product) {
          product.selected = checkbox.checked;
          this._selected.set(String(product.id), checkbox.checked);
        }

        checkbox.closest(".rokform-rebuy-addons__item")?.classList.toggle(
          "rokform-rebuy-addons__item--selected",
          checkbox.checked
        );
        this._notifyChange();
        return;
      }

      const swatch = event.target.closest(".rokform-rebuy-addons__size-input");
      if (!swatch) return;

      const product = this._products.find((p) => String(p.id) === String(swatch.dataset.productId));
      const variant = product?.variants?.find((v) => String(v.id) === String(swatch.value));
      if (!product || !variant) return;

      product.selected_variant_id = variant.id;
      product.selected_variant = variant;
      this._catalogSignature = addonCatalogSignature(this._products);

      const index = this._products.indexOf(product);
      if (!this._updateAddonItem(product, index)) {
        this._render();
      }

      this._notifyChange();
    }

    _onClick(event) {
      const trigger = event.target.closest("[data-action='open-lightbox']");
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();

      const product = this._products[parseInt(trigger.dataset.index || "", 10)];
      if (product) openAddonLightbox(product, this._widgetConfig?.discount);
    }

    _notifyChange() {
      this.dispatchEvent(new CustomEvent("rokform-rebuy-addons:change", { bubbles: true }));
    }

    getSelectedProducts() {
      return this._products.filter((p) => p.selected);
    }

    getSelectedCartItems() {
      return this.getSelectedProducts()
        .map((product) => buildRebuyAddonCartItem(product, this._rebuyWidgetId, this._widgetConfig?.discount))
        .filter(Boolean);
    }

    getSubtotalCents() {
      return this.getSelectedProducts().reduce((sum, product) => {
        const variant = getSelectedVariant(product);
        if (!variant) return sum;
        return sum + getAddonVariantPricing(product, variant, this._widgetConfig?.discount).saleCents;
      }, 0);
    }
  }

  class RokformRebuyAddonsGroup extends HTMLElement {
    constructor() {
      super();
      this._onPanelState = this._onPanelState.bind(this);
      this._panelDetails = new Map();
    }

    connectedCallback() {
      this._skeletonEl = this.querySelector("[data-group-skeleton]");
      this._emptyEl = this.querySelector("[data-group-empty]");
      this._errorEl = this.querySelector("[data-group-error]");
      this._panels = [...this.querySelectorAll("rokform-rebuy-addons")];

      this.addEventListener("rokform-rebuy-addons:panel-state", this._onPanelState);
      this.classList.add("is-loading");
      if (this._skeletonEl) this._skeletonEl.hidden = false;
    }

    disconnectedCallback() {
      this.removeEventListener("rokform-rebuy-addons:panel-state", this._onPanelState);
    }

    _onPanelState(event) {
      const { panel, loading, productCount, errorMessage } = event.detail || {};
      if (!panel) return;

      this._panelDetails.set(panel, { loading, productCount, errorMessage: errorMessage || "" });
      this._updateChrome();
    }

    _updateChrome() {
      const panels = this._panels.length ? this._panels : [...this.querySelectorAll("rokform-rebuy-addons")];
      const details = panels.map((panel) => this._panelDetails.get(panel)).filter(Boolean);
      const allReported = details.length === panels.length;
      const anyLoading = details.some((d) => d.loading);
      const totalProducts = details.reduce((sum, d) => sum + (d.productCount || 0), 0);
      const firstError = details.find((d) => d.errorMessage)?.errorMessage || "";

      this.classList.toggle("is-loading", anyLoading || !allReported);
      if (this._skeletonEl) this._skeletonEl.hidden = allReported && !anyLoading;

      if (this._errorEl) {
        if (firstError) {
          this._errorEl.textContent = firstError;
          this._errorEl.hidden = false;
        } else {
          this._errorEl.hidden = true;
          this._errorEl.textContent = "";
        }
      }

      if (this._emptyEl) {
        this._emptyEl.hidden = !allReported || anyLoading || totalProducts > 0;
      }

      this.classList.toggle("has-addons", totalProducts > 0);
    }

    getSelectedProducts() {
      return this._panels.flatMap((panel) => panel.getSelectedProducts());
    }
  }

  if (!customElements.get("rokform-rebuy-addons")) {
    customElements.define("rokform-rebuy-addons", RokformRebuyAddons);
  }

  if (!customElements.get("rokform-rebuy-addons-group")) {
    customElements.define("rokform-rebuy-addons-group", RokformRebuyAddonsGroup);
  }

  window.rokformRebuyAddonsElement = () =>
    document.querySelector("rokform-rebuy-addons-group") || document.querySelector("rokform-rebuy-addons");

  window.rokformRebuyAddonsElements = getAddonPanels;

  window.rokformGetRebuyAddonSubtotalCents = () =>
    getAddonPanels().reduce((sum, panel) => sum + panel.getSubtotalCents(), 0);

  window.rokformGetRebuyAddonCartItems = () =>
    getAddonPanels().flatMap((panel) => panel.getSelectedCartItems());
  window.rokformBuildMainCartItemFromFormData = buildMainCartItemFromFormData;
  window.rokformOpenRebuyAddonLightbox = openAddonLightbox;
})();
